if (!customElements.get('announcement-rotator')) {
  customElements.define(
    'announcement-rotator',
    class AnnouncementRotator extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.intervalId = null;
        this.isUserPaused = false;
        this.isVisible = true;
        this.isTabActive = true;
        this.abortController = null;
      }

      connectedCallback() {
        this.slides = this.querySelectorAll('[data-slide]');
        this.liveRegion = this.querySelector('[data-live-region]');

        this.animation = this.dataset.animation || 'fade';
        this.itemDuration = (parseInt(this.dataset.itemDuration, 10) || 5) * 1000;
        this.transitionDuration = parseInt(this.dataset.transitionDuration, 10) || 500;
        this.isStatic = this.dataset.static === 'true';
        this.blockCount = this.slides.length;
        this.messageSelector = this.dataset.messageSelector || '[data-message]';

        if (this.isStatic || this.blockCount < 2) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.initializeSlides();
        this.bindEvents(signal);
        this.observeVisibility(signal);
        this.startRotation();
      }

      disconnectedCallback() {
        this.stopRotation();
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      }

      initializeSlides() {
        this.slides.forEach((slide, index) => {
          slide.dataset.active = index === 0 ? 'true' : 'false';
          slide.dataset.leaving = 'false';
        });
      }

      bindEvents(signal) {
        this.addEventListener('mouseenter', () => this.userPause(), { signal });
        this.addEventListener('mouseleave', () => this.userResume(), { signal });

        this.addEventListener('focusin', () => this.userPause(), { signal });
        this.addEventListener('focusout', (e) => {
          if (!this.contains(e.relatedTarget)) this.userResume();
        }, { signal });

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.goToSlide(this.currentIndex - 1);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.goToSlide(this.currentIndex + 1);
          }
        }, { signal });

        // Touch/swipe support
        let touchStartX = 0;
        const minSwipeDistance = 50;

        this.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
        }, { signal, passive: true });

        this.addEventListener('touchend', (e) => {
          const touchEndX = e.changedTouches[0].screenX;
          const distance = touchEndX - touchStartX;

          if (Math.abs(distance) > minSwipeDistance) {
            if (distance > 0) {
              this.goToSlide(this.currentIndex - 1);
            } else {
              this.goToSlide(this.currentIndex + 1);
            }
          }
        }, { signal, passive: true });

        // Respect reduced motion preference
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mediaQuery.matches) {
          this.transitionDuration = 0;
        }
        mediaQuery.addEventListener('change', (e) => {
          this.transitionDuration = e.matches ? 0 : parseInt(this.dataset.transitionDuration, 10) || 500;
        }, { signal });

        // Stop/restart when tab visibility changes (prevents throttled intervals)
        document.addEventListener('visibilitychange', () => {
          this.isTabActive = !document.hidden;
          this.updateRotationState();
        }, { signal });
      }

      observeVisibility(signal) {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isVisible = entry.isIntersecting;
              this.updateRotationState();
            });
          },
          { threshold: 0.1 }
        );

        this.observer.observe(this);

        signal.addEventListener('abort', () => {
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        });
      }

      updateRotationState() {
        const shouldRun = this.isVisible && this.isTabActive && !this.isUserPaused;

        if (shouldRun) {
          this.startRotation();
        } else {
          this.stopRotation();
        }
      }

      startRotation() {
        if (this.intervalId) return;
        this.intervalId = setInterval(() => {
          this.next();
        }, this.itemDuration);
      }

      stopRotation() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }

      userPause() {
        this.isUserPaused = true;
        this.updateRotationState();
      }

      userResume() {
        this.isUserPaused = false;
        this.updateRotationState();
      }

      next() {
        const nextIndex = (this.currentIndex + 1) % this.blockCount;
        this.goToSlide(nextIndex);
      }

      goToSlide(index) {
        if (index < 0) index = this.blockCount - 1;
        if (index >= this.blockCount) index = 0;
        if (index === this.currentIndex) return;

        const currentSlide = this.slides[this.currentIndex];
        const nextSlide = this.slides[index];

        // Update current slide
        currentSlide.dataset.leaving = 'true';
        currentSlide.dataset.active = 'false';
        currentSlide.setAttribute('aria-hidden', 'true');

        const currentLink = currentSlide.querySelector('a');
        if (currentLink) currentLink.setAttribute('tabindex', '-1');

        // Update next slide
        nextSlide.dataset.active = 'true';
        nextSlide.dataset.leaving = 'false';
        nextSlide.setAttribute('aria-hidden', 'false');

        const nextLink = nextSlide.querySelector('a');
        if (nextLink) nextLink.removeAttribute('tabindex');

        // Update live region for screen readers
        if (this.liveRegion) {
          const slideText = nextSlide.querySelector(this.messageSelector)?.textContent?.trim() || '';
          this.liveRegion.textContent = slideText;
        }

        // Reset positioning after transition
        setTimeout(() => {
          currentSlide.dataset.leaving = 'false';
          currentSlide.classList.remove('tw-relative');
          currentSlide.classList.add('tw-absolute', 'tw-inset-0', 'tw-pointer-events-none');
          nextSlide.classList.remove('tw-absolute', 'tw-inset-0', 'tw-pointer-events-none');
          nextSlide.classList.add('tw-relative');
        }, this.transitionDuration);

        this.currentIndex = index;

        // Reset interval
        this.stopRotation();
        this.startRotation();
      }
    }
  );
}